import { create } from 'zustand';

export type WidgetView = 'lobby' | 'thread' | 'domina' | 'new-chat';

/**
 * Which Domina the user is talking to:
 *  - 'agent' = Domina Agent: FREE, closed/hard-wired, no API tokens. Default.
 *  - 'ai'    = Domina AI: paid LLM (reports, copy, images, analysis).
 */
export type DominaMode = 'agent' | 'ai';

interface ChatWidgetState {
  isOpen: boolean;
  view: WidgetView;
  activeRoomId: string | null;
  /** Agent vs AI. Starts on the free Agent; user toggles up to AI. */
  dominaMode: DominaMode;
  /** A message to drop into the Agent when the widget opens (home typewriter intro). */
  pendingAgentPrompt: string | null;
  toggle: () => void;
  open: () => void;
  close: () => void;
  /** Open straight to the workspace-chats list. */
  openChats: () => void;
  /** Open straight to Domina (keeps the current mode — Agent by default). */
  openDomina: () => void;
  /** Open straight to Domina Agent, optionally seeding a first user message. */
  openAgent: (prompt?: string) => void;
  setDominaMode: (mode: DominaMode) => void;
  /** Read & clear the pending prompt (Agent consumes it once on mount). */
  consumePendingPrompt: () => string | null;
  goToLobby: () => void;
  goToThread: (roomId: string) => void;
  goToDomina: () => void;
  goToNewChat: () => void;
}

export const useChatWidgetStore = create<ChatWidgetState>((set, get) => ({
  isOpen: false,
  view: 'lobby',
  activeRoomId: null,
  dominaMode: 'agent',
  pendingAgentPrompt: null,

  toggle: () =>
    set((s) => ({
      isOpen: !s.isOpen,
      // Reset to lobby when opening
      ...(s.isOpen ? {} : { view: 'lobby', activeRoomId: null }),
    })),
  open: () => set({ isOpen: true, view: 'lobby', activeRoomId: null }),
  close: () => set({ isOpen: false }),
  openChats: () => set({ isOpen: true, view: 'lobby', activeRoomId: null }),
  openDomina: () => set({ isOpen: true, view: 'domina', activeRoomId: null }),
  openAgent: (prompt?: string) =>
    set({
      isOpen: true,
      view: 'domina',
      dominaMode: 'agent',
      activeRoomId: null,
      pendingAgentPrompt: prompt?.trim() ? prompt.trim() : null,
    }),
  setDominaMode: (mode) => set({ dominaMode: mode }),
  consumePendingPrompt: () => {
    const p = get().pendingAgentPrompt;
    if (p) set({ pendingAgentPrompt: null });
    return p;
  },

  goToLobby: () => set({ view: 'lobby', activeRoomId: null }),
  goToThread: (roomId: string) => set({ view: 'thread', activeRoomId: roomId }),
  goToDomina: () => set({ view: 'domina', activeRoomId: null }),
  goToNewChat: () => set({ view: 'new-chat', activeRoomId: null }),
}));
